/**
 * Publieke embeddable Pomodoro Timer pagina.
 * Gated flow:
 *  1) Intro/gate (naam + zakelijke e-mail) -> /start -> Lead aanmaken
 *  2) Profielkeuze (Standard 25/5, Deep Work 50/10, Custom)
 *  3) Focus-fase (minimalistische circulaire SVG countdown, document.title)
 *  4) Alarm-fase (geluid loopt tot user op "Start pauze" klikt)
 *  5) Pauze-fase (Vitality Coach met dynamische interventies)
 *  6) Dashboard met focus-statistieken
 *
 * Volledig in lijn met de andere Tool Hub-tools (Meeting Timer, Ebook
 * Library, Vitaliteit Benchmark): light mode, shadcn-ui, oranje accent,
 * framer-motion voor fase-overgangen.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { pomodoroTimerAPI } from '@/lib/api';
import {
  User, Mail, Building2, Loader2, AlertCircle, Lock, Sparkles,
  Play, Pause, RotateCcw, ChevronRight, Coffee, Brain, Settings2,
  Wind, Activity, Droplets, MoveRight, Eye, Volume2, BellOff,
  Check, Zap, Timer as TimerIcon, BarChart3, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  playEndSound, unlockAudio, startLoopingAlarm, stopLoopingAlarm,
} from '@/lib/timerAudio';
import BookingCta from '@/components/BookingCta';

const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_buyer-match-26/artifacts/52px3160_zynq_logo_icon_e0dde9b0559f42738516da0422c8a0d0.png';

const ICON_FOR_TYPE = {
  breathing: Wind,
  stretch: Activity,
  hydration: Droplets,
  movement: MoveRight,
  mindset: Brain,
};

function pad(n) {
  return String(Math.max(0, Math.floor(n))).padStart(2, '0');
}
function formatTime(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${pad(m)}:${pad(sec)}`;
}

export default function PomodoroTimerPage() {
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Gate state — alles is geblokkeerd tot user submitted heeft
  const [unlocked, setUnlocked] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [contact, setContact] = useState({ name: '', email: '', company: '' });
  const [gateError, setGateError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Phase: 'gate' | 'profile' | 'focus' | 'alarm' | 'break' | 'break_alarm' | 'dashboard'
  const [phase, setPhase] = useState('gate');

  // Profile / timer setup
  const [profileKey, setProfileKey] = useState('standard');
  const [focusMin, setFocusMin] = useState(25);
  const [breakMin, setBreakMin] = useState(5);

  // Active timer state
  const [remaining, setRemaining] = useState(25 * 60);
  const [running, setRunning] = useState(false);
  const endAtRef = useRef(null);
  const intervalRef = useRef(null);
  const originalTitleRef = useRef('');

  // Stats
  const [stats, setStats] = useState({
    completedFocusBlocks: 0,
    completedBreaks: 0,
    totalFocusSeconds: 0,
    totalBreakSeconds: 0,
    interruptions: 0,
  });
  const [focusQuality, setFocusQuality] = useState(null);
  const [breakQuality, setBreakQuality] = useState(null);

  // Vitality Coach
  const [interventionIdx, setInterventionIdx] = useState(0);

  // Force light mode op publieke pagina
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    root.classList.remove('dark');
    originalTitleRef.current = document.title;
    return () => {
      if (hadDark) root.classList.add('dark');
      document.title = originalTitleRef.current || 'Pomodoro Timer';
      stopLoopingAlarm();
    };
  }, []);

  // Load config
  useEffect(() => {
    pomodoroTimerAPI.getConfig()
      .then((res) => {
        setConfig(res.data);
        // Pak standaard profiel als initieel
        const profiles = res.data?.profiles || [];
        const def = profiles.find((p) => p.key === 'standard') || profiles[0];
        if (def) {
          setProfileKey(def.key);
          setFocusMin(def.focus_min);
          setBreakMin(def.break_min);
          setRemaining(def.focus_min * 60);
        }
      })
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 403) {
          setLoadError('Deze tool is momenteel niet actief.');
        } else {
          setLoadError('Kon de tool niet laden. Probeer het later opnieuw.');
        }
      })
      .finally(() => setLoadingConfig(false));
  }, []);

  const accentColor = config?.accent_color || '#ea580c';

  // ── Timer interval — drift-vrij via vaste eindtijd ──
  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      if (!endAtRef.current) return;
      const left = Math.round((endAtRef.current - Date.now()) / 1000);
      if (left <= 0) {
        setRemaining(0);
        setRunning(false);
        endAtRef.current = null;
        if (phase === 'focus') {
          // Einde focus -> alarm laat loopen, ga naar alarm-state
          setStats((s) => ({
            ...s,
            completedFocusBlocks: s.completedFocusBlocks + 1,
            totalFocusSeconds: s.totalFocusSeconds + focusMin * 60,
          }));
          startLoopingAlarm(config?.sound_key || 'bell');
          setPhase('alarm');
        } else if (phase === 'break') {
          // Einde pauze -> alarm laat loopen, klaar voor volgende focus
          setStats((s) => ({
            ...s,
            completedBreaks: s.completedBreaks + 1,
            totalBreakSeconds: s.totalBreakSeconds + breakMin * 60,
          }));
          startLoopingAlarm(config?.sound_key || 'bell');
          setPhase('break_alarm');
        }
      } else {
        setRemaining(left);
      }
    }, 250);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, phase, focusMin, breakMin, config]);

  // Document title bijhouden
  useEffect(() => {
    if (phase === 'focus' && running) {
      document.title = `${formatTime(remaining)} · Focus`;
    } else if (phase === 'break' && running) {
      document.title = `${formatTime(remaining)} · Pauze`;
    } else if (phase === 'alarm') {
      document.title = '⏰ Tijd voor pauze!';
    } else if (phase === 'break_alarm') {
      document.title = '⏰ Pauze voorbij — terug naar focus!';
    } else {
      document.title = originalTitleRef.current || 'Pomodoro Timer';
    }
  }, [remaining, running, phase]);

  // Vitality interventie roteren tijdens pauze (elke 25 sec automatisch)
  useEffect(() => {
    if (phase !== 'break') return;
    const interventions = config?.interventions || [];
    if (interventions.length <= 1) return;
    const id = setInterval(() => {
      setInterventionIdx((i) => (i + 1) % interventions.length);
    }, 25000);
    return () => clearInterval(id);
  }, [phase, config]);

  // ── Email-validatie helper ──
  const validateBusinessEmail = (email) => {
    const trimmed = (email || '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return 'Vul een geldig e-mailadres in.';
    }
    if (!config?.require_business_email) return '';
    const domain = trimmed.split('@')[1];
    const blocked = (config?.blocked_email_domains || []).map((d) => d.toLowerCase());
    if (blocked.includes(domain)) {
      return 'Gebruik een zakelijk e-mailadres (geen Gmail, Hotmail, etc.).';
    }
    return '';
  };

  // ── Gate submit ──
  const handleUnlock = async () => {
    setGateError('');
    if (!contact.name.trim()) {
      setGateError('Vul je naam in om verder te gaan.');
      return;
    }
    const emailErr = validateBusinessEmail(contact.email);
    if (emailErr) {
      setGateError(emailErr);
      return;
    }

    setSubmitting(true);
    try {
      unlockAudio(); // ontgrendel audio context voor latere alarm-loops
      const res = await pomodoroTimerAPI.start({
        contact,
        profile_key: profileKey,
        focus_min: focusMin,
        break_min: breakMin,
      });
      setSessionId(res.data?.session_id);
      setUnlocked(true);
      setPhase('profile');
    } catch (e) {
      setGateError(e?.response?.data?.detail || 'Versturen mislukt. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Profile selectie ──
  const handleSelectProfile = (p) => {
    setProfileKey(p.key);
    setFocusMin(p.focus_min);
    setBreakMin(p.break_min);
    setRemaining(p.focus_min * 60);
  };

  const handleCustomChange = (focus, brk) => {
    const f = Math.max(1, Math.min(240, Number(focus) || 1));
    const b = Math.max(1, Math.min(120, Number(brk) || 1));
    setFocusMin(f);
    setBreakMin(b);
    setRemaining(f * 60);
  };

  const handleStartFocus = () => {
    setRemaining(focusMin * 60);
    endAtRef.current = Date.now() + focusMin * 60 * 1000;
    setRunning(true);
    setPhase('focus');
  };

  const handlePauseTimer = () => {
    setRunning(false);
    setStats((s) => ({ ...s, interruptions: s.interruptions + 1 }));
  };

  const handleResumeTimer = () => {
    endAtRef.current = Date.now() + remaining * 1000;
    setRunning(true);
  };

  const handleAbandonFocus = () => {
    if (!window.confirm('Weet je zeker dat je deze focus-sessie wilt afbreken?')) return;
    setRunning(false);
    endAtRef.current = null;
    setStats((s) => ({ ...s, interruptions: s.interruptions + 1 }));
    setPhase('profile');
  };

  // Alarm → start pauze
  const handleStartBreak = () => {
    stopLoopingAlarm();
    setInterventionIdx(Math.floor(Math.random() * (config?.interventions?.length || 1)));
    setRemaining(breakMin * 60);
    endAtRef.current = Date.now() + breakMin * 60 * 1000;
    setRunning(true);
    setPhase('break');
  };

  // Alarm na pauze → volgende focus of klaar
  const handleNextFocus = () => {
    stopLoopingAlarm();
    setRemaining(focusMin * 60);
    endAtRef.current = Date.now() + focusMin * 60 * 1000;
    setRunning(true);
    setPhase('focus');
  };

  const handleSkipBreak = () => {
    stopLoopingAlarm();
    if (running) setRunning(false);
    setPhase('break_alarm'); // direct naar overgangs-scherm zonder alarm
    stopLoopingAlarm();
  };

  const handleFinishSession = async () => {
    stopLoopingAlarm();
    setRunning(false);
    setPhase('dashboard');
    if (!sessionId) return;
    try {
      await pomodoroTimerAPI.complete(sessionId, {
        completed_focus_blocks: stats.completedFocusBlocks,
        completed_breaks: stats.completedBreaks,
        total_focus_seconds: stats.totalFocusSeconds,
        total_break_seconds: stats.totalBreakSeconds,
        interruptions: stats.interruptions,
        focus_quality: focusQuality,
        break_quality: breakQuality,
      });
    } catch (e) {
      // niet kritiek — dashboard wordt sowieso getoond
    }
  };

  const handleNewSession = () => {
    setStats({
      completedFocusBlocks: 0,
      completedBreaks: 0,
      totalFocusSeconds: 0,
      totalBreakSeconds: 0,
      interruptions: 0,
    });
    setFocusQuality(null);
    setBreakQuality(null);
    setRemaining(focusMin * 60);
    setPhase('profile');
  };

  // ─── Render ──────────────────────────────────────────────────────────
  if (loadingConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50/40">
        <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50/40 p-6 text-slate-800">
        <div className="text-center max-w-md">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <p className="text-slate-700">{loadError}</p>
        </div>
      </div>
    );
  }

  const minimalist = phase === 'focus' && running;

  return (
    <div
      className={`min-h-screen text-slate-800 transition-colors duration-700 ${
        minimalist
          ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950'
          : phase === 'break' || phase === 'break_alarm'
          ? 'bg-gradient-to-br from-emerald-50 via-white to-teal-50/40'
          : 'bg-gradient-to-br from-orange-50 via-white to-amber-50/40'
      } py-8 px-4`}
      data-testid="pomodoro-timer-page"
    >
      <div className="max-w-3xl mx-auto">
        {/* Header — alleen tonen als niet in minimalist focus mode */}
        {!minimalist && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_ICON} alt="Zynq" className="w-10 h-10 rounded-lg" />
              <div>
                <div className="font-bold text-lg leading-none text-slate-900">Zynq</div>
                <div className="text-xs text-slate-500">Pomodoro Timer</div>
              </div>
            </div>
            {unlocked && phase !== 'gate' && phase !== 'dashboard' && (
              <button
                onClick={handleFinishSession}
                className="text-xs text-slate-500 hover:text-orange-700 underline"
                data-testid="finish-session-btn"
              >
                Sessie afronden
              </button>
            )}
          </div>
        )}

        <AnimatePresence mode="wait">
          {phase === 'gate' && (
            <motion.div
              key="gate"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <GateStep
                config={config}
                contact={contact}
                setContact={setContact}
                error={gateError}
                submitting={submitting}
                onUnlock={handleUnlock}
                accentColor={accentColor}
              />
            </motion.div>
          )}

          {phase === 'profile' && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <ProfileStep
                config={config}
                profileKey={profileKey}
                focusMin={focusMin}
                breakMin={breakMin}
                onSelect={handleSelectProfile}
                onCustomChange={handleCustomChange}
                onStart={handleStartFocus}
                stats={stats}
              />
            </motion.div>
          )}

          {(phase === 'focus' || phase === 'alarm') && (
            <motion.div
              key="focus"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.4 }}
            >
              <FocusStep
                phase={phase}
                remaining={remaining}
                running={running}
                focusMin={focusMin}
                onPause={handlePauseTimer}
                onResume={handleResumeTimer}
                onAbandon={handleAbandonFocus}
                onStartBreak={handleStartBreak}
                accentColor={accentColor}
                minimalist={minimalist}
              />
            </motion.div>
          )}

          {(phase === 'break' || phase === 'break_alarm') && (
            <motion.div
              key="break"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.4 }}
            >
              <BreakStep
                phase={phase}
                remaining={remaining}
                breakMin={breakMin}
                interventions={config?.interventions || []}
                interventionIdx={interventionIdx}
                onCycleIntervention={() => {
                  const list = config?.interventions || [];
                  if (list.length) setInterventionIdx((i) => (i + 1) % list.length);
                }}
                onSkipBreak={handleSkipBreak}
                onNextFocus={handleNextFocus}
                onFinishSession={handleFinishSession}
              />
            </motion.div>
          )}

          {phase === 'dashboard' && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
            >
              <DashboardStep
                stats={stats}
                focusQuality={focusQuality}
                setFocusQuality={setFocusQuality}
                breakQuality={breakQuality}
                setBreakQuality={setBreakQuality}
                onNewSession={handleNewSession}
                sessionId={sessionId}
                config={config}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {!minimalist && (
          <div className="mt-10 text-center text-xs text-slate-500">
            <p>© Zynq · Focus + herstel = duurzame productiviteit</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Subcomponenten ─────────────────────────────────────────────────────
function GateStep({ config, contact, setContact, error, submitting, onUnlock, accentColor }) {
  return (
    <div data-testid="step-gate">
      <div className="text-center mb-6">
        {config?.intro_badge && (
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold mb-4">
            <Sparkles className="w-3.5 h-3.5" /> {config.intro_badge}
          </div>
        )}
        <div className="inline-flex w-14 h-14 rounded-2xl items-center justify-center mb-4 shadow-lg shadow-orange-600/20"
             style={{ backgroundColor: accentColor }}>
          <TimerIcon className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-slate-900">
          {config?.intro_title || 'Pomodoro Timer'}
        </h1>
        <p className="text-slate-600 max-w-xl mx-auto">
          {config?.intro_subtitle || 'Werk in gefocuste blokken en herstel actief tijdens je pauze.'}
        </p>
      </div>

      <Card className="border-orange-100 shadow-sm">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-md bg-amber-50 border border-amber-100">
            <Lock className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                {config?.gate_title || 'Krijg directe toegang'}
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                {config?.gate_subtitle || 'Vul kort je gegevens in om de timer te ontgrendelen.'}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 font-medium flex items-center gap-2">
              <User className="w-4 h-4 text-orange-600" /> Naam <span className="text-red-500">*</span>
            </Label>
            <Input
              data-testid="gate-name-input"
              placeholder="Jouw naam"
              value={contact.name}
              onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
              className="bg-white border-slate-200 focus-visible:ring-orange-500"
              autoComplete="name"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 font-medium flex items-center gap-2">
              <Mail className="w-4 h-4 text-orange-600" /> Zakelijk e-mailadres <span className="text-red-500">*</span>
            </Label>
            <Input
              data-testid="gate-email-input"
              type="email"
              placeholder="jij@bedrijf.nl"
              value={contact.email}
              onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
              className="bg-white border-slate-200 focus-visible:ring-orange-500"
              autoComplete="email"
            />
            {config?.require_business_email && (
              <p className="text-[11px] text-slate-400">
                Gebruik je werk-e-mail. Persoonlijke providers (Gmail, Hotmail, etc.) worden niet geaccepteerd.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 font-medium flex items-center gap-2">
              <Building2 className="w-4 h-4 text-orange-600" /> Bedrijf <span className="text-slate-400 text-xs font-normal">(optioneel)</span>
            </Label>
            <Input
              data-testid="gate-company-input"
              placeholder="Bedrijfsnaam"
              value={contact.company}
              onChange={(e) => setContact((c) => ({ ...c, company: e.target.value }))}
              className="bg-white border-slate-200 focus-visible:ring-orange-500"
              autoComplete="organization"
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded-md border border-red-100 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            onClick={onUnlock}
            disabled={submitting}
            data-testid="gate-unlock-btn"
            className="w-full text-white"
            style={{ backgroundColor: accentColor }}
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig…</>
            ) : (
              <><Lock className="w-4 h-4 mr-2" /> {config?.cta_unlock || 'Ontgrendel de timer'}</>
            )}
          </Button>
          <p className="text-[11px] text-slate-400 text-center">
            We slaan je gegevens veilig op om contact met je op te kunnen nemen.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function ProfileStep({ config, profileKey, focusMin, breakMin, onSelect, onCustomChange, onStart, stats }) {
  const profiles = config?.profiles || [];
  const currentIsCustom = profileKey === 'custom';

  return (
    <div data-testid="step-profile">
      <div className="text-center mb-6">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2 text-slate-900">
          Kies je <span className="text-orange-600">focus-profiel</span>
        </h2>
        <p className="text-slate-600">Hoe wil je deze sessie werken?</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        {profiles.map((p) => {
          const active = profileKey === p.key;
          return (
            <button
              key={p.key}
              type="button"
              data-testid={`profile-${p.key}`}
              onClick={() => onSelect(p)}
              className={`text-left p-4 rounded-xl border-2 transition-all ${
                active
                  ? 'border-orange-500 bg-orange-50 shadow-md ring-2 ring-orange-100'
                  : 'border-slate-200 bg-white hover:border-orange-300 hover:bg-orange-50/40'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                {p.key === 'deep_work' ? (
                  <Brain className="w-4 h-4 text-violet-600" />
                ) : p.key === 'custom' ? (
                  <Settings2 className="w-4 h-4 text-sky-600" />
                ) : (
                  <Zap className="w-4 h-4 text-orange-600" />
                )}
                <span className="font-bold text-slate-900">{p.label}</span>
              </div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
                {p.focus_min}m focus · {p.break_min}m pauze
              </div>
              {p.description && (
                <p className="text-xs text-slate-600 leading-snug">{p.description}</p>
              )}
            </button>
          );
        })}
      </div>

      {currentIsCustom && (
        <Card className="border-sky-100 mb-5">
          <CardContent className="p-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm text-slate-700">Focus-duur (minuten)</Label>
                <Input
                  type="number"
                  min="1"
                  max="240"
                  value={focusMin}
                  onChange={(e) => onCustomChange(e.target.value, breakMin)}
                  data-testid="custom-focus-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm text-slate-700">Pauze-duur (minuten)</Label>
                <Input
                  type="number"
                  min="1"
                  max="120"
                  value={breakMin}
                  onChange={(e) => onCustomChange(focusMin, e.target.value)}
                  data-testid="custom-break-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {(stats.completedFocusBlocks > 0 || stats.completedBreaks > 0) && (
        <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-around text-center">
          <div>
            <div className="text-lg font-bold text-orange-600">{stats.completedFocusBlocks}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Focus-blokken</div>
          </div>
          <div className="w-px h-8 bg-slate-200" />
          <div>
            <div className="text-lg font-bold text-emerald-600">{stats.completedBreaks}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Pauzes</div>
          </div>
          <div className="w-px h-8 bg-slate-200" />
          <div>
            <div className="text-lg font-bold text-slate-900">
              {Math.round(stats.totalFocusSeconds / 60)}m
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Focus-tijd</div>
          </div>
        </div>
      )}

      <Button
        onClick={onStart}
        data-testid="start-focus-btn"
        size="lg"
        className="w-full bg-orange-600 hover:bg-orange-700 text-white"
      >
        <Play className="w-5 h-5 mr-2" /> Start {focusMin} min focus
      </Button>
      <p className="text-[11px] text-slate-400 text-center mt-2">
        Tijdens focus is je interface minimalistisch zodat je niet wordt afgeleid.
      </p>
    </div>
  );
}

function FocusStep({
  phase, remaining, running, focusMin, onPause, onResume, onAbandon, onStartBreak, accentColor, minimalist
}) {
  const totalSec = Math.max(1, focusMin * 60);
  const progress = ((totalSec - remaining) / totalSec); // 0..1
  const isAlarm = phase === 'alarm';

  return (
    <div data-testid="step-focus" className="flex flex-col items-center">
      {!minimalist && !isAlarm && (
        <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Focus-sessie</p>
      )}

      {isAlarm && (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="mb-4 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 text-orange-800 text-sm font-bold animate-pulse">
            <Volume2 className="w-4 h-4" /> Tijd voor pauze!
          </div>
        </motion.div>
      )}

      <CircularCountdown
        remaining={remaining}
        totalSec={totalSec}
        progress={progress}
        color={isAlarm ? '#ea580c' : minimalist ? '#fb923c' : accentColor}
        minimalist={minimalist}
        pulse={isAlarm}
      />

      {minimalist && (
        <p className="mt-6 text-xs uppercase tracking-widest text-slate-400">
          Focus · Blijf hier · Geen afleiding
        </p>
      )}

      {!isAlarm && (
        <div className="flex items-center gap-3 mt-8">
          {running ? (
            <Button
              onClick={onPause}
              variant="outline"
              size="lg"
              data-testid="focus-pause-btn"
              className={minimalist ? 'border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white' : ''}
            >
              <Pause className="w-4 h-4 mr-2" /> Pauzeer
            </Button>
          ) : (
            <Button
              onClick={onResume}
              size="lg"
              data-testid="focus-resume-btn"
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Play className="w-4 h-4 mr-2" /> Hervat
            </Button>
          )}
          <Button
            onClick={onAbandon}
            variant="ghost"
            size="lg"
            data-testid="focus-abandon-btn"
            className={minimalist ? 'text-slate-500 hover:text-slate-300' : 'text-slate-500'}
          >
            Afbreken
          </Button>
        </div>
      )}

      {isAlarm && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mt-8 flex flex-col items-center gap-2"
        >
          <Button
            onClick={onStartBreak}
            size="lg"
            data-testid="start-break-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 shadow-lg"
          >
            <BellOff className="w-4 h-4 mr-2" /> Stop alarm & Start pauze
          </Button>
          <p className="text-xs text-slate-500 max-w-xs text-center">
            Het alarm blijft loopen totdat je op deze knop drukt. Tijd om actief te herstellen.
          </p>
        </motion.div>
      )}
    </div>
  );
}

function BreakStep({
  phase, remaining, breakMin, interventions, interventionIdx,
  onCycleIntervention, onSkipBreak, onNextFocus, onFinishSession,
}) {
  const totalSec = Math.max(1, breakMin * 60);
  const progress = ((totalSec - remaining) / totalSec);
  const isAlarm = phase === 'break_alarm';

  const intervention = interventions[interventionIdx % interventions.length] || null;
  const IconComp = intervention ? (ICON_FOR_TYPE[intervention.type] || Coffee) : Coffee;

  return (
    <div data-testid="step-break" className="flex flex-col items-center">
      <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-2 flex items-center gap-1">
        <Coffee className="w-3.5 h-3.5" /> Vitality Coach · Pauze
      </p>

      {isAlarm && (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="mb-4 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 text-sm font-bold animate-pulse">
            <Volume2 className="w-4 h-4" /> Pauze voorbij — klaar voor focus?
          </div>
        </motion.div>
      )}

      <CircularCountdown
        remaining={remaining}
        totalSec={totalSec}
        progress={progress}
        color="#10b981"
        minimalist={false}
        pulse={isAlarm}
        label="Pauze"
      />

      {!isAlarm && intervention && (
        <motion.div
          key={intervention.id || interventionIdx}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.4 }}
          className="mt-6 w-full"
        >
          <Card className="border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50/40 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-200/70 flex items-center justify-center flex-shrink-0">
                  <IconComp className="w-6 h-6 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                    <p className="text-xs font-bold uppercase tracking-wider text-emerald-800">
                      {intervention.type === 'breathing' ? 'Ademhaling' :
                       intervention.type === 'stretch' ? 'Stretch' :
                       intervention.type === 'hydration' ? 'Hydratatie' :
                       intervention.type === 'movement' ? 'Beweging' :
                       intervention.type === 'mindset' ? 'Mindset' : 'Tip'}
                    </p>
                    {interventions.length > 1 && (
                      <button
                        onClick={onCycleIntervention}
                        data-testid="cycle-intervention-btn"
                        className="text-[11px] text-emerald-700 hover:text-emerald-900 underline"
                      >
                        Volgende interventie
                      </button>
                    )}
                  </div>
                  <h3 className="font-bold text-slate-900 text-base mb-1.5">{intervention.title}</h3>
                  <p className="text-sm text-slate-700 leading-relaxed">{intervention.body}</p>
                  {intervention.duration_sec && (
                    <p className="text-[11px] text-emerald-700 mt-2 font-medium">
                      ⏱️ Indicatie: {Math.round(intervention.duration_sec / 5) * 5} sec
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <div className="flex items-center gap-3 mt-6 flex-wrap justify-center">
        {!isAlarm ? (
          <>
            <Button
              onClick={onSkipBreak}
              variant="outline"
              size="lg"
              data-testid="skip-break-btn"
            >
              Pauze overslaan
            </Button>
            <Button
              onClick={onFinishSession}
              variant="ghost"
              size="lg"
              data-testid="finish-from-break-btn"
              className="text-slate-500"
            >
              Sessie afronden
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={onNextFocus}
              size="lg"
              data-testid="next-focus-btn"
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 shadow-lg"
            >
              <BellOff className="w-4 h-4 mr-2" /> Stop alarm & volgende focus
            </Button>
            <Button
              onClick={onFinishSession}
              variant="outline"
              size="lg"
              data-testid="finish-from-break-alarm-btn"
            >
              Sessie afronden
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function CircularCountdown({ remaining, totalSec, progress, color, minimalist, pulse, label }) {
  // Math voor stroke
  const size = 280;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dashOffset = useMemo(() => c * (1 - progress), [c, progress]);

  return (
    <div className={`relative ${pulse ? 'animate-pulse' : ''}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="rotate-[-90deg]">
        {/* Background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={minimalist ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'}
          strokeWidth={stroke}
        />
        {/* Progress ring */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={dashOffset}
          style={{ transition: 'stroke-dashoffset 0.4s linear' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div
          className={`text-6xl md:text-7xl font-extrabold tabular-nums tracking-tight ${
            minimalist ? 'text-white' : 'text-slate-900'
          }`}
          data-testid="circular-timer-display"
        >
          {formatTime(remaining)}
        </div>
        <div className={`text-[10px] uppercase tracking-[0.2em] mt-1 ${
          minimalist ? 'text-slate-400' : 'text-slate-500'
        }`}>
          {label || (minimalist ? 'Focus' : 'Tijd over')}
        </div>
      </div>
    </div>
  );
}

function DashboardStep({
  stats, focusQuality, setFocusQuality, breakQuality, setBreakQuality, onNewSession, sessionId, config,
}) {
  const totalFocusMin = Math.round(stats.totalFocusSeconds / 60);
  const totalBreakMin = Math.round(stats.totalBreakSeconds / 60);
  const ratio = stats.totalFocusSeconds + stats.totalBreakSeconds > 0
    ? Math.round((stats.totalFocusSeconds / (stats.totalFocusSeconds + stats.totalBreakSeconds)) * 100)
    : 0;

  const efficiency = stats.completedFocusBlocks > 0
    ? Math.max(0, 100 - stats.interruptions * 10)
    : 0;

  const StarRow = ({ value, onChange, label, color = 'orange' }) => (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-700">{label}</Label>
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`${label.toLowerCase().replace(/ /g, '-')}-${n}`}
            onClick={() => onChange(n)}
            className={`w-9 h-9 rounded-md text-base font-bold transition-all ${
              value && n <= value
                ? color === 'emerald'
                  ? 'bg-emerald-500 text-white shadow-sm'
                  : 'bg-orange-500 text-white shadow-sm'
                : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="step-dashboard">
      <div className="text-center mb-6">
        <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-600 items-center justify-center mb-3 shadow-lg shadow-emerald-600/20">
          <BarChart3 className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-1 text-slate-900">
          Sessie afgerond
        </h2>
        <p className="text-slate-600">Hoe productief was deze sessie?</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Focus-blokken" value={stats.completedFocusBlocks} accent="text-orange-600" />
        <StatCard label="Pauzes" value={stats.completedBreaks} accent="text-emerald-600" />
        <StatCard label="Focus (min)" value={totalFocusMin} accent="text-slate-900" />
        <StatCard label="Onderbrekingen" value={stats.interruptions} accent="text-amber-600" />
      </div>

      <Card className="border-slate-100 mb-5">
        <CardContent className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-sm font-semibold text-slate-700">Focus-ratio</Label>
              <span className="text-sm font-bold text-orange-600">{ratio}%</span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-orange-500 to-amber-500"
                initial={{ width: 0 }}
                animate={{ width: `${ratio}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              {totalFocusMin} min focus vs {totalBreakMin} min herstel
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-sm font-semibold text-slate-700">Geschatte efficiëntie</Label>
              <span className="text-sm font-bold text-emerald-600">{efficiency}%</span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-emerald-500 to-teal-500"
                initial={{ width: 0 }}
                animate={{ width: `${efficiency}%` }}
                transition={{ duration: 0.8, delay: 0.1 }}
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Hoe minder onderbrekingen, hoe hoger je efficiëntie.
            </p>
          </div>

          <div className="border-t border-slate-100 pt-4 grid sm:grid-cols-2 gap-4">
            <StarRow
              value={focusQuality}
              onChange={setFocusQuality}
              label="Focus-kwaliteit"
              color="orange"
            />
            <StarRow
              value={breakQuality}
              onChange={setBreakQuality}
              label="Pauze-kwaliteit"
              color="emerald"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button
          onClick={onNewSession}
          size="lg"
          data-testid="new-session-btn"
          className="bg-orange-600 hover:bg-orange-700 text-white"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Nieuwe sessie
        </Button>
      </div>

      <div className="mt-6">
        <BookingCta config={config} variant="orange" />
      </div>

      {sessionId && (
        <p className="text-center text-[10px] text-slate-400 mt-4">Sessie: {sessionId}</p>
      )}
    </div>
  );
}

function StatCard({ label, value, accent = 'text-slate-900' }) {
  return (
    <div className="bg-white rounded-lg border border-slate-100 p-3 text-center shadow-sm">
      <div className={`text-2xl font-extrabold tabular-nums ${accent}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
